export type EntityTypes =
  | 'benefitSponsorsGroups'
  | 'benefitSponsors'
  | 'groups'
  | 'products'
  | 'contacts'
  | 'alternateIds'
  | 'allowedGroupRelations'
  | 'externalGroupRelations'
  | 'reports';

export type EntityRow = Record<string, string>;

export interface TransformedData<T extends Partial<EntityRow>> {
  type: EntityTypes;
  data: RawTransformData<T>;
}

// Transform Template
export interface RawTransformData<T extends Partial<EntityRow>> {
  rows: T[];
  firstLine: string[];
  errors?: TransformFieldValidationError[];
  firstDataLine: string[];
}

export type RawTransformMap = Partial<Record<EntityTypes, RawTransformData<EntityRow>>>;

export interface TransformPackage {
  Template: Template;
  CsvData: Array<TransformedData<EntityRow>>;
  TemplateName?: string;
  JobZipFileName?: string;
}

export interface TemplateDirectiveConfig {
  operation: string;
  args: any[];
}

export interface TemplateDirective {
  [name: string]: TemplateDirectiveConfig[];
}

export interface TemplateDefault {
  [name: string]: string;
}

export interface TemplateConfig {
  hasHeaders: boolean;
  autoApprove?: boolean;
  updateMask?: TemplateUpdateMask;
}

export interface TemplateUpdateMaskConfig {
  [name: string]: boolean;
}
export interface HeaderConfig {
  displayName: string;
  objectName: string;
  canUpdate: boolean;
  updateDisabled: boolean;
  validation: string[];
}

export interface SettingsConfig {
  assumedRows: boolean;
  updateMask: boolean;
}

export interface EntityTransformResponse<T extends Partial<EntityRow>> {
  errors: string[];
  failures: string[];
  firstLine: string[];
  firstDataLine: string[];
  rows: T[];
}

export type TransformResponse = Partial<Record<EntityTypes, RawTransformData<EntityRow>>>;
export type Overrides = {
  [K in EntityTypes]?: Record<string, string>;
};
export type Disabled = {
  [K in EntityTypes]?: Record<string, string>;
};
export type TemplateEntities = Record<EntityTypes, TemplateDirective>;
export type TemplateEntitiesDefaults = Record<EntityTypes | 'global', TemplateDefault> & {
  overrides?: Overrides;
  disabled?: Disabled;
};
export type TemplateUpdateMask = Record<EntityTypes, TemplateUpdateMaskConfig>;

export interface LoaderFlow {
  blockName: string;
  args?: string;
}

export interface LoaderConfig {
  name: string;
  config: LoaderFlow[];
}

export type Template = {
  config: TemplateConfig;
  defaults?: TemplateEntitiesDefaults;
  assumed?: Record<EntityTypes, Array<Record<string, string>>>;
  flow?: LoaderConfig;
} & TemplateEntities;

export interface TransformedFileResult {
  rows: object[];
  firstLine: string[];
  firstDataLine: string[];
  failures: object[];
  errors: TransformFieldValidationError[];
}

export interface TransformFieldValidationError {
  row: number;
  field: string;
  error: string[];
}

export interface DefaultSchemaField {
  key: string;
  type: string;
  value?: string;
  values?: { value: string; display: string }[];
}

export interface FormikFieldObj extends DefaultSchemaField {
  label: string;
  name: string;
  options?: { value: string; label: string }[];
}
export interface EntityOverrides {
  [entityType: string]: Record<string, string>; // Each entityType maps to an object of key-value pairs
}
export interface EntityDisabled {
  [entityType: string]: Record<string, string>; // Each entityType maps to an object of key-value pairs
}
export interface EntityConfigurationData {
  [key: string]: { [key: string]: string } | EntityOverrides; // Allow for other entity configurations
  overrides: EntityOverrides; // `overrides` must always be present
  disabled: EntityDisabled; // `disabled` must always be present
}

export interface DefaultEntitySchema {
  [x: string]: DefaultSchemaField[];
}

enum DefaultsKeys {
  benefitSponsors = 'benefitSponsors',
  groups = 'groups',
  products = 'products'
}

export interface DefaultEntityAPI {
  defaults: {
    [key in DefaultsKeys]: { fields: DefaultSchemaField[] };
  };
}

export enum JobStatus {
  Approved = 'approved',
  Denied = 'denied',
  Stopped = 'stopped'
}

export interface CustomFieldPreview {
  result: string;
  steps: string[];
}

export interface ReferenceData {
  fields: Partial<Record<EntityTypes, string[]>>;
  rows: Partial<Record<EntityTypes, string[]>>;
  entityType: string;
}
